import React, { memo } from "react";
import { useTranslation } from "react-i18next";
import { IconCheck, IconLogout, IconBan, IconLockOpen, IconSquareCheck } from "@tabler/icons-react";
import { DeviceTab, DEVICE_TAB_BLOCKED } from "const/deviceManagement.const";

interface BulkActionBarProps {
  selectedCount: number;
  totalDevices: number;
  onLogout: () => void;
  onBlock: () => void;
  onUnblock: () => void;
  isSelectAllMode: boolean;
  onToggleSelectAll: () => void;
  activeTab: DeviceTab;
}

/**
 * Bulk action bar - always visible.
 * Shows selected count + select-all toggle on the left, action buttons on the right.
 * Buttons have solid background colors that change visual state based on enable/disable.
 */
const BulkActionBar: React.FC<BulkActionBarProps> = ({
  selectedCount,
  totalDevices,
  onLogout,
  onBlock,
  onUnblock,
  isSelectAllMode,
  onToggleSelectAll,
  activeTab,
}) => {
  const { t } = useTranslation("deviceManagement");
  const hasSelection = isSelectAllMode || selectedCount > 0;
  const isBlockedTab = activeTab === DEVICE_TAB_BLOCKED;

  return (
    <div className="d-flex align-items-center justify-content-between px-2 py-1">
      <div className="d-flex align-items-center gap-2">
        <span className="text-body fw-medium">
          {t("FM_DEVICE_SELECTED_COUNT", { count: selectedCount })}
        </span>
        <button
          type="button"
          className={`btn ${isSelectAllMode ? "btn-primary" : "btn-secondary"} btn-sm rounded-pill px-3 ms-2 d-flex align-items-center gap-1`}
          onClick={onToggleSelectAll}
        >
          {isSelectAllMode ? <IconSquareCheck size={16} /> : <IconCheck size={16} />}
          <span>{isSelectAllMode ? t("TXT_DEVICE_DESELECT_ALL") : t("TXT_DEVICE_SELECT_ALL")}</span>
        </button>
      </div>

      <div className="d-flex gap-2">
        {!isBlockedTab && (
          <button
            type="button"
            className="btn btn-warning text-white d-flex align-items-center gap-1 shadow-sm"
            onClick={onLogout}
            disabled={!hasSelection}
          >
            <IconLogout size={16} />
            <span className="fw-medium">{t("TXT_DEVICE_ACTION_LOGOUT")}</span>
          </button>
        )}
        {!isBlockedTab && (
          <button
            type="button"
            className="btn btn-danger text-white d-flex align-items-center gap-1 shadow-sm"
            onClick={onBlock}
            disabled={!hasSelection}
          >
            <IconBan size={16} />
            <span className="fw-medium">{t("TXT_DEVICE_ACTION_BLOCK")}</span>
          </button>
        )}
        {isBlockedTab && (
          <button
            type="button"
            className="btn btn-success text-white d-flex align-items-center gap-1 shadow-sm"
            onClick={onUnblock}
            disabled={!hasSelection}
          >
            <IconLockOpen size={16} />
            <span className="fw-medium">{t("TXT_DEVICE_ACTION_UNBLOCK")}</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default memo(BulkActionBar);
